import React, { useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress,
  IconButton,
  Paper,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { createSnowIceRemovalLog, uploadPhotos } from '../api';
import Swal from 'sweetalert2';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';

const DropZone = styled(Paper)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : '#ccc'}`,
  borderRadius: 12,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isDragActive ? `${theme.palette.primary.light}10` : 'transparent',
  transition: 'all 0.3s',
}));

const validationSchema = Yup.object({
  date: Yup.date().required('Date is required'),
  time_started: Yup.string().required('Start time is required'),
  time_completed: Yup.string().required('End time is required'),
  temp_and_conditions: Yup.string().required('Temperature & conditions are required'),
  comments: Yup.string(),
  tasks: Yup.object({
    sidewalks: Yup.boolean(),
    all_entrances: Yup.boolean(),
    stairs: Yup.boolean(),
    ramp: Yup.boolean(),
    amenity: Yup.boolean(),
  }).test('at-least-one-task', 'At least one task must be selected', (value) => {
    return Object.values(value || {}).some(v => v === true);
  }),
});

const CreateSnowIceRemovalLogForm = ({ propertyId, buildingName, street, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      date: new Date().toISOString().split('T')[0],
      time_started: '',
      time_completed: '',
      temp_and_conditions: '',
      comments: '',
      tasks: {
        sidewalks: false,
        all_entrances: false,
        stairs: false,
        ramp: false,
        amenity: false,
      },
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (photos.length === 0) {
        Swal.fire('Warning', 'Please upload at least one photo', 'warning');
        return;
      }

      setSubmitting(true);
      try {
        const uploadedPhotoUrls = photos
          .filter(p => p.uploaded)
          .map(p => ({
            url: p.uploaded.fileUrl,
            key: p.uploaded.fileKey,
            name: p.file.name,
          }));

        const payload = {
          date: values.date,
          day: new Date(values.date).toLocaleDateString('en-US', { weekday: 'long' }),
          time_started: values.time_started,
          time_completed: values.time_completed,
          temp_and_conditions: values.temp_and_conditions,
          comments: values.comments,
          tasks: values.tasks,
          property_details: {
            property_id: propertyId,
            building_name: buildingName,
            street: street,
          },
          pictures_uploaded: uploadedPhotoUrls,
        };

        await createSnowIceRemovalLog(payload);

        Swal.fire({
          title: 'Success!',
          text: 'Snow/Ice removal log created successfully!',
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
        });

        resetForm();
        setPhotos([]);
        if (onSuccess) onSuccess();
      } catch (error) {
        Swal.fire('Error', error.message || 'Failed to submit log', 'error');
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Handle file selection
  const handleFiles = async (files) => {
    const newFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (newFiles.length === 0) {
      toast.error('Please select valid image files');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploaded = await uploadPhotos(
        propertyId,
        newFiles,
        (percent) => setUploadProgress(percent)
      );

      const photosWithPreview = newFiles.map((file, idx) => ({
        file,
        preview: URL.createObjectURL(file),
        uploaded: uploaded[idx],
      }));

      setPhotos(prev => [...prev, ...photosWithPreview]);
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 1000);
    } catch (err) {
      Swal.fire('Upload Failed', 'Could not upload photos. Try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Drag & Drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
        <FeatherIcon icon="snowflake" size={28} />
        Create Snow/Ice Removal Log
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Property: <strong>{buildingName}</strong> – {street}
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Date & Times */}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Date of Service" name="date" type="date" {...formik.getFieldProps('date')}
              error={formik.touched.date && Boolean(formik.errors.date)}
              helperText={formik.touched.date && formik.errors.date}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Time Started" name="time_started" type="time" {...formik.getFieldProps('time_started')}
              error={formik.touched.time_started && Boolean(formik.errors.time_started)}
              helperText={formik.touched.time_started && formik.errors.time_started}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Time Completed" name="time_completed" type="time" {...formik.getFieldProps('time_completed')}
              error={formik.touched.time_completed && Boolean(formik.errors.time_completed)}
              helperText={formik.touched.time_completed && formik.errors.time_completed}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Temperature & Weather" name="temp_and_conditions" placeholder="e.g. -5°C, light snow"
              {...formik.getFieldProps('temp_and_conditions')}
              error={formik.touched.temp_and_conditions && Boolean(formik.errors.temp_and_conditions)}
              helperText={formik.touched.temp_and_conditions && formik.errors.temp_and_conditions}
            />
          </Grid>

          {/* Tasks */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Tasks Performed <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
              {Object.keys(formik.values.tasks).map(task => (
                <FormControlLabel key={task}
                  control={<Checkbox checked={formik.values.tasks[task]}
                    onChange={e => formik.setFieldValue(`tasks.${task}`, e.target.checked)} />}
                  label={task === 'all_entrances' ? 'All Entrances' : task.charAt(0).toUpperCase() + task.slice(1).replace('_', ' ')}
                />
              ))}
            </Box>
            {formik.touched.tasks && formik.errors.tasks && typeof formik.errors.tasks === 'string' && (
              <Alert severity="error" sx={{ mt: 1 }}>{formik.errors.tasks}</Alert>
            )}
          </Grid>

          {/* Photo Upload */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Photos <span style={{ color: 'red' }}>*</span>
            </Typography>

            <DropZone
              isDragActive={dragActive}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <FeatherIcon icon="upload-cloud" size={48} color="#666" />
              <Typography variant="h6" sx={{ mt: 2 }}>
                {dragActive ? "Drop photos here" : "Drag & drop photos or click to select"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Up to 20 images (JPG, PNG, WebP)
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                hidden
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
              />
            </DropZone>

            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            {photos.length > 0 && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {photos.map((photo, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={idx}>
                    <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                      <img src={photo.preview} alt="preview" style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                      {photo.uploaded ? (
                        <FeatherIcon icon="check-circle" color="green" size={24} style={{ position: 'absolute', top: 8, right: 8 }} />
                      ) : (
                        <CircularProgress size={24} style={{ position: 'absolute', top: 8, right: 8 }} />
                      )}
                      <IconButton size="small" onClick={() => removePhoto(idx)}
                        sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}>
                        <FeatherIcon icon="x" size={16} />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          {/* Comments */}
          <Grid item xs={12}>
            <TextField fullWidth label="Additional Comments (Optional)" name="comments" multiline rows={4}
              {...formik.getFieldProps('comments')}
              placeholder="Any issues, salt used, equipment notes, etc."
            />
          </Grid>

          {/* Submit */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || uploading || photos.length === 0}
              startIcon={submitting ? <CircularProgress size={20} /> : <FeatherIcon icon="check-circle" size={20} />}
              sx={{ minWidth: 220, py: 1.5 }}
            >
              {submitting ? 'Submitting Log...' : 'Submit Snow Removal Log'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default CreateSnowIceRemovalLogForm;